import type { ReactNode } from 'react';
import type { JevAutoBudgetView, RankingMigrationJobView, RankingMigrationStatus, RankingMigrationView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Popover, usePopover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneChip, type Tone } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useJevAutoBudget, useRaiseMigrationBudget, useRankingMigration, useRetryMigrationJob, useSetJevAutoBudget } from '../api/migration-api';
import { centsOf, dollarsOf, microUsdOfDollars } from '../money';
import styles from './migration.module.css';

// ADR 0062 §6.2, mockup M4 (ADMIN-17): the one-off move of open jobs to the ideal candidate, with the limit the HR Lead approved, and
// the daily limit on Jev reading new CVs by itself. Both read figures the server works out from the asks; nothing here is stored.

const LIMIT_PRESETS_DOLLARS: readonly number[] = [25, 50, 100, 250];
const LIMIT_CHOICES = 3;
const DAILY_PRESETS_DOLLARS: readonly number[] = [5, 10, 20, 50];
const PERCENT = 100;

const MIGRATION_LEAD = 'Open jobs were converted for free and are being read by Jev, busiest first, two at a time. Closed jobs got a profile too and keep their old order until someone presses “Rank with Jev”.';
const AUTO_LEAD = 'New CVs are read on their own, so rankings are ready when HR opens a job. Only new CVs are read, nothing is paid twice.';

const STATUS_CHIPS: Readonly<Record<RankingMigrationStatus, { readonly tone: Tone; readonly label: string }>> = {
  moved: { tone: 'done', label: 'Moved' },
  reading: { tone: 'progress', label: 'Reading' },
  waiting_for_budget: { tone: 'attention', label: 'Waiting for budget' },
  failed: { tone: 'rejected', label: 'Couldn’t finish' },
  closed_not_read: { tone: 'ended', label: 'Closed' },
};

function percentOf(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(PERCENT, Math.round((PERCENT * part) / whole));
}

function Bar({ percent, tone, label }: { readonly percent: number; readonly tone?: 'warn' | 'bad'; readonly label: string }): ReactNode {
  return (
    <div className={styles.bar} data-tone={tone} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={PERCENT} aria-valuenow={percent}>
      <span className={styles.barFill} style={{ inlineSize: `${percent}%` }} />
    </div>
  );
}

// ---- Moving to the ideal candidate ----

export function MigrationCard(): ReactNode {
  const query = useRankingMigration();
  const view = query.data;
  if (query.isPending) {
    return (
      <Card title="Moving to the ideal candidate">
        <SkeletonLines count={4} />
      </Card>
    );
  }
  if (!view) {
    return (
      <Card title="Moving to the ideal candidate" meta="The progress didn’t load." action={<Button size="sm" onClick={() => void query.refetch()}>Try again</Button>} />
    );
  }
  // Nothing to move (a new install, or every job moved and the list since cleared) still draws the card, so the Admin
  // can see the limit and that nothing is waiting (Admin redesign 2026-11-02: the card no longer vanishes).
  return <MigrationBody view={view} />;
}

function MigrationBody({ view }: { readonly view: RankingMigrationView }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const needed = Math.max(0, view.spentMicroUsd + view.estimateLeftMicroUsd - view.approvedMicroUsd);
  const isLimited = view.waitingForBudget > 0;
  const moneyNote = isLimited ? `The next job would pass the limit. About ${dollarsOf(needed)} more is needed.` : `About ${dollarsOf(view.estimateLeftMicroUsd)} still to spend (estimate)`;
  return (
    <Card title="Moving to the ideal candidate" meta={MIGRATION_LEAD} action={canEdit ? <RaiseLimit approved={view.approvedMicroUsd} /> : undefined}>
      <div className={styles.stats}>
        <Stat value={view.moved} label="Jobs moved" />
        <Stat value={view.reading} label="Reading now" />
        <Stat value={view.waitingForBudget} label="Waiting for budget" tone={view.waitingForBudget > 0 ? 'warn' : undefined} />
        <Stat value={view.failed} label="Failed" tone={view.failed > 0 ? 'bad' : undefined} />
        {view.closedNotRead > 0 && <Stat value={view.closedNotRead} label="Closed, not read" />}
      </div>
      <div className={styles.money}>
        <span>
          Spent so far <span className={styles.big}>{dollarsOf(view.spentMicroUsd)}</span> of <b>{dollarsOf(view.approvedMicroUsd)}</b> approved
        </span>
        <span className={styles.muted}>{moneyNote}</span>
      </div>
      <Bar percent={percentOf(view.spentMicroUsd, view.approvedMicroUsd)} tone={isLimited ? 'warn' : undefined} label="Spent of the approved limit" />
      {view.jobs.length === 0 ? (
        <p className={styles.muted}>No jobs to move. Every job already ranks by its ideal candidate.</p>
      ) : (
        <ul className={styles.jobs}>
          {view.jobs.map((job) => (
            <JobRow key={job.jobId} job={job} canRetry={canEdit} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function Stat({ value, label, tone }: { readonly value: number; readonly label: string; readonly tone?: 'warn' | 'bad' | undefined }): ReactNode {
  return (
    <div className={styles.stat} data-tone={tone}>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

function JobRow({ job, canRetry }: { readonly job: RankingMigrationJobView; readonly canRetry: boolean }): ReactNode {
  const retry = useRetryMigrationJob();
  const show = useToasts((toasts) => toasts.show);
  const chip = STATUS_CHIPS[job.status];
  return (
    <li className={styles.job}>
      <b className={styles.jobTitle}>{job.title}</b>
      <span className={styles.jobDetail}>{detailOf(job)}</span>
      <span className={styles.jobStatus}>
        <ToneChip tone={chip.tone}>{chip.label}</ToneChip>
        {job.status === 'failed' && canRetry && (
          <Button
            size="sm"
            variant="quiet"
            isPending={retry.isPending}
            onClick={() => retry.mutate(job.jobId, { onError: (error) => show({ tone: 'danger', message: 'Couldn’t start it again', detail: errorMessage(error) }) })}
          >
            Retry
          </Button>
        )}
      </span>
    </li>
  );
}

function detailOf(job: RankingMigrationJobView): ReactNode {
  const note = job.notes[0];
  switch (job.status) {
    case 'reading':
      return (
        <span className={styles.progress}>
          <span className={styles.num}>
            {job.done.toLocaleString('en-US')} of {job.total.toLocaleString('en-US')} CVs
          </span>
          <Bar percent={percentOf(job.done, job.total)} label={`${job.title}: CVs read`} />
        </span>
      );
    case 'waiting_for_budget':
      return `Estimate ${centsOf(job.estimateMicroUsd)} · would pass the limit`;
    case 'failed':
      return 'Jev stopped answering after 3 tries';
    case 'closed_not_read':
      return 'Closed job · keeps its old order until someone presses “Rank with Jev”';
    case 'moved':
      return [`${job.total.toLocaleString('en-US')} CVs`, centsOf(job.estimateMicroUsd), note].filter(Boolean).join(' · ');
  }
}

function RaiseLimit({ approved }: { readonly approved: number }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const raise = useRaiseMigrationBudget();
  const show = useToasts((toasts) => toasts.show);
  const choices = LIMIT_PRESETS_DOLLARS.map(microUsdOfDollars).filter((micro) => micro > approved).slice(0, LIMIT_CHOICES);
  const options = choices.length > 0 ? choices : [approved * 2];

  const approve = (micro: number): void => {
    close();
    raise.mutate(micro, {
      onSuccess: () => show({ tone: 'success', message: `The limit is now ${dollarsOf(micro)}`, detail: 'Jobs waiting for budget start reading.' }),
      onError: (error) => show({ tone: 'danger', message: 'The limit wasn’t raised', detail: errorMessage(error) }),
    });
  };

  return (
    <>
      <Button ref={triggerRef} variant="solid" size="sm" aria-haspopup="dialog" aria-expanded={isOpen} onClick={toggle}>
        Raise limit
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label="Approve a higher limit">
        <div className={styles.pop}>
          <strong>Approve a higher limit</strong>
          <span className={styles.muted}>HR sets how much Jev may spend on the move. Now {dollarsOf(approved)}.</span>
          <div className={styles.popActions}>
            {options.map((micro) => (
              <Button key={micro} size="sm" onClick={() => approve(micro)}>
                {dollarsOf(micro, 0)}
              </Button>
            ))}
          </div>
        </div>
      </Popover>
    </>
  );
}

// ---- Jev automatic reading ----

export function AutoReadingCard(): ReactNode {
  const query = useJevAutoBudget();
  const view = query.data;
  if (query.isPending) {
    return (
      <Card title="Jev automatic reading">
        <SkeletonLines count={2} />
      </Card>
    );
  }
  if (!view) {
    return <Card title="Jev automatic reading" meta="The daily limit didn’t load." action={<Button size="sm" onClick={() => void query.refetch()}>Try again</Button>} />;
  }
  return <AutoReadingBody view={view} />;
}

function AutoReadingBody({ view }: { readonly view: JevAutoBudgetView }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const note = view.isPaused
    ? 'Limit reached. New CVs wait, and reading resumes after midnight in Dhaka. Rank again and Save and rank still work.'
    : 'Resets at midnight in Dhaka.';
  return (
    <Card title="Jev automatic reading" meta={AUTO_LEAD} action={canEdit ? <EditDailyLimit daily={view.dailyMicroUsd} /> : undefined}>
      <div className={styles.money}>
        <span>
          <span className={styles.big}>{dollarsOf(view.spentTodayMicroUsd)}</span> of <b>{dollarsOf(view.dailyMicroUsd)}</b> today
        </span>
        <span className={styles.muted}>{note}</span>
      </div>
      <Bar percent={percentOf(view.spentTodayMicroUsd, view.dailyMicroUsd)} tone={view.isPaused ? 'bad' : undefined} label="Spent of today’s limit" />
    </Card>
  );
}

function EditDailyLimit({ daily }: { readonly daily: number }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const save = useSetJevAutoBudget();
  const show = useToasts((toasts) => toasts.show);

  const set = (micro: number): void => {
    close();
    save.mutate(micro, { onError: (error) => show({ tone: 'danger', message: 'The daily limit wasn’t saved', detail: errorMessage(error) }) });
  };

  return (
    <>
      <Button ref={triggerRef} size="sm" aria-haspopup="dialog" aria-expanded={isOpen} onClick={toggle}>
        Edit daily limit
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label="Edit the daily limit">
        <div className={styles.pop}>
          <strong>Daily limit for automatic reading</strong>
          <span className={styles.muted}>Now {dollarsOf(daily)} a day. Rank again and Save and rank are never held back by it.</span>
          <div className={styles.popActions}>
            {DAILY_PRESETS_DOLLARS.map((dollars) => (
              <Button key={dollars} size="sm" variant={microUsdOfDollars(dollars) === daily ? 'solid' : 'outline'} onClick={() => set(microUsdOfDollars(dollars))}>
                {dollarsOf(microUsdOfDollars(dollars), 0)}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => set(0)}>
              Stop reading by itself
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
